import React from 'react';
import { Tag, Typography, Progress, Button, Skeleton } from 'antd';
import { ClockCircleOutlined, DeleteOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { Project } from '../../services/retailerApi';

dayjs.extend(relativeTime);

interface ProjectCardProps {
  project: Project;
  isActive?: boolean;
  onClick: (projectId: string) => void;
  onDelete: (projectId: string) => void;
  onDragStart: (e: React.DragEvent, projectId: string) => void;
  interlocutorName?: string;
  isRefreshing?: boolean;
}

const ProjectCard: React.FC<ProjectCardProps> = ({
  project,
  isActive,
  onClick,
  onDelete,
  onDragStart,
  interlocutorName,
  isRefreshing = false,
}) => {
  const snapshotStatus = (() => {
    const raw = project.metadata?.snapshotPayload;
    if (!raw) return project.pushPullStatus;
    try {
      const parsed = JSON.parse(raw);
      return parsed?.snapshot?.push_pull_status || project.pushPullStatus;
    } catch {
      return project.pushPullStatus;
    }
  })();
  const isPush = snapshotStatus === 'push';
  const displayFields = (project.customFields || []).filter((field) =>
    (project.customFieldsDisplay || []).includes(field.key)
  );
  const progress = (() => {
    const board = project.taskBoard || {};
    const allTasks = [...(board.todo || []), ...(board.in_progress || [])];
    if (!allTasks.length) return 0;
    const done = allTasks.filter((task) => task.status === 'archived').length;
    return Math.round((done / allTasks.length) * 100);
  })();

  if (isRefreshing) {
    return (
      <div
        className="operational-project-card"
        style={{
          border: '1px solid #e2e8f0',
          borderRadius: '12px',
          marginBottom: '12px',
          background: '#ffffff',
          padding: '16px',
        }}
      >
        <Skeleton active title={{ width: '60%' }} paragraph={{ rows: 3 }} />
      </div>
    );
  }

  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, project._id || '')}
      className={`operational-project-card ${isActive ? 'active' : ''}`}
      onClick={() => onClick(project._id!)}
      style={{ 
        border: isActive ? '1px solid #3b82f6' : '1px solid #e2e8f0',
        borderRadius: '12px',
        marginBottom: '12px',
        background: isActive ? '#f0f9ff' : '#ffffff',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        boxShadow: isActive ? '0 4px 6px -1px rgba(59, 130, 246, 0.1)' : '0 1px 3px 0 rgba(0, 0, 0, 0.05)',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {project.priority === 'high' && (
        <div style={{ 
          position: 'absolute', 
          top: 0, 
          left: 0, 
          width: '4px', 
          height: '100%', 
          background: '#ef4444' 
        }} />
      )}
      <div style={{ padding: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
          <Typography.Text strong style={{ fontSize: 14, color: '#0f172a', flex: 1, marginRight: 8, lineHeight: 1.4 }}>
            {project.title}
          </Typography.Text>
          <Tag 
            color={isPush ? 'orange' : 'blue'} 
            style={{ 
              margin: 0, 
              fontSize: 10, 
              borderRadius: 6, 
              fontWeight: 700,
              border: 'none',
              padding: '0 8px'
            }}
          >
            {isPush ? 'PUSH' : 'PULL'}
          </Tag>
        </div>

        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
            <ClockCircleOutlined style={{ fontSize: 12, color: '#94a3b8' }} />
            <Typography.Text type="secondary" style={{ fontSize: 12, color: '#64748b' }}>
              {project.lastEmailAt ? dayjs(project.lastEmailAt).fromNow() : 'Pas d\'activité'}
            </Typography.Text>
          </div>
          
          <div style={{ background: '#f8fafc', borderRadius: 8, padding: '10px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
              <Typography.Text style={{ fontSize: 11, fontWeight: 700, color: '#475569', letterSpacing: '0.02em' }}>PROGRESSION</Typography.Text>
              <Typography.Text style={{ fontSize: 11, fontWeight: 700, color: '#3b82f6' }}>
                {project.unreadEmailCount && project.unreadEmailCount > 0 ? `${project.unreadEmailCount} non lus` : 'À jour'}
              </Typography.Text>
            </div>
            <Progress
              percent={progress}
              size="small"
              showInfo={false}
              strokeColor="#3b82f6"
              trailColor="#e2e8f0"
              style={{ margin: 0 }}
            />
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 700, marginBottom: 2 }}>INTERLOCUTEUR</div>
            <Typography.Text strong style={{ fontSize: 12, color: '#1e293b', display: 'block' }} ellipsis>
              {interlocutorName || '—'}
            </Typography.Text>
            {displayFields.length > 0 && (
              <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {displayFields.map((field) => (
                  <Tag key={field.key} style={{ margin: 0, fontSize: 10 }}>
                    {field.label}: {Array.isArray(field.value) ? field.value.join(', ') : field.value ?? '—'}
                  </Tag>
                ))}
              </div>
            )}
          </div>
          
          <div style={{ display: 'flex', gap: 8 }}>
            <Button 
              danger 
              type="text" 
              size="small"
              icon={<DeleteOutlined style={{ fontSize: 14 }} />} 
              onClick={(e) => { e.stopPropagation(); onDelete(project._id!); }}
              style={{ padding: '0 4px', color: '#94a3b8' }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectCard;
